import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllClients } from "../../../data/getters";
import clientApi from "../../../data/api/clientApi";
import { selectClient } from "../../../data/store/actions/clientStoreFunctions";
import { useLanguage } from "../../helpers/LanguageContext";

export default function useClientsPageController() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [clientIdToDelete, setClientIdToDelete] = useState(null);
  const [clientIdToModify, setClientIdToModify] = useState(null);
  const [showClientUpdateModal, setShowClientUpdateModal] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await dispatch(getAllClients()));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleRowClick = useCallback(
    (clientId) => {
      setLoading(true);
      try {
        dispatch(selectClient(clientId));
        navigate(`/clientAnalyzer/${clientId}`);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, navigate]
  );

  const handleModifyClient = useCallback((clientId) => {
    setClientIdToModify(clientId);
    setShowClientUpdateModal(true);
  }, []);

  const handleClientUpdateClose = useCallback(() => {
    setShowClientUpdateModal(false);
    setClientIdToModify(null);
  }, []);

  const handleClientUpdate = useCallback(
    async (updatedClientData) => {
      await dispatch(
        clientApi.updateClientApi(clientIdToModify, updatedClientData)
      );
      handleClientUpdateClose();
      await loadClients();
    },
    [clientIdToModify, dispatch, handleClientUpdateClose, loadClients]
  );

  const handleDeleteClient = useCallback((clientId) => {
    setClientIdToDelete(clientId);
    setShowDeleteConfirmation(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    await dispatch(clientApi.deleteClientApi(clientIdToDelete));
    setShowDeleteConfirmation(false);
    setClientIdToDelete(null);
    await loadClients();
  }, [clientIdToDelete, dispatch, loadClients]);

  return {
    t,
    clients,
    loading,
    showNewClient,
    showClientUpdateModal,
    showDeleteConfirmation,
    clientIdToDelete,
    clientIdToModify,
    openNewClient: () => setShowNewClient(true),
    closeNewClient: async () => {
      setShowNewClient(false);
      await loadClients();
    },
    handleRowClick,
    handleModifyClient,
    handleClientUpdateClose,
    handleClientUpdate,
    handleDeleteClient,
    handleConfirmDelete,
    handleCancelDelete: () => setShowDeleteConfirmation(false)
  };
}
